import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Flame, Search, ShieldCheck, Timer, Truck, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { setMeta } from "../lib/seo";
import { useCountdown } from "../lib/useCountdown";
import { ProductCard } from "../components/ProductCard";
import { AdSlot } from "../components/AdSlot";
import { NewsletterForm } from "../components/NewsletterForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1607082350899-7e105aa886ae?crop=entropy&cs=srgb&fm=jpg&q=85";

const SectionHead = ({ title, sub, to, linkLabel, testid }) => (
  <div className="mb-5 flex items-end justify-between gap-4">
    <div>
      <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 sm:text-2xl md:text-3xl">{title}</h2>
      {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
    </div>
    {to && (
      <Link
        to={to}
        data-testid={testid}
        className="flex shrink-0 items-center gap-1 text-sm font-bold text-gold-600 hover:text-gold-700"
      >
        {linkLabel} <ArrowRight size={15} />
      </Link>
    )}
  </div>
);

export default function HomePage() {
  const countdown = useCountdown();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };
  useEffect(() => {
    setMeta(
      "ShoppinMagic — Trending Deals & Top Products, Found Like Magic",
      "Shopping, but make it magic. ShoppinMagic curates top-rated products and real daily deals from trusted merchants across electronics, fashion, beauty, home, and more."
    );
  }, []);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: api.getCategories });
  const { data: trending = [] } = useQuery({
    queryKey: ["products", "trending"],
    queryFn: () => api.getProducts({ tag: "trending", sort: "trending", limit: 8 }),
  });
  const { data: deals = [] } = useQuery({
    queryKey: ["products", "deals"],
    queryFn: () => api.getProducts({ tag: "deal", sort: "discount", limit: 8 }),
  });
  const { data: recommended = [] } = useQuery({
    queryKey: ["products", "recommended"],
    queryFn: () => api.getProducts({ tag: "recommended", limit: 8 }),
  });
  const { data: amazonFinds = [] } = useQuery({
    queryKey: ["products", "amazon-find"],
    queryFn: () => api.getProducts({ tag: "amazon-find", limit: 40 }),
  });
  const { data: posts = [] } = useQuery({
    queryKey: ["blog", "home"],
    queryFn: () => api.getBlogPosts(),
  });

  return (
    <div data-testid="home-page">
      <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
        <AdSlot slot="top-banner" minH="min-h-[60px] md:min-h-[90px]" />
      </div>

      {/* Hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 md:grid-cols-2 md:py-16 lg:px-8" data-testid="hero-section">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-50 px-3 py-1 text-xs font-bold text-gold-700">
            <ShieldCheck size={13} /> Curated from trusted merchants
          </span>
          <h1 className="font-heading mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Shopping, but <span className="text-gold-600">make it magic.</span>
          </h1>
          <p className="mt-4 max-w-lg text-base text-slate-600 sm:text-lg">
            We sift through thousands of products across Amazon, Temu, AliExpress, and more — then surface only the
            top-rated finds and real deals worth your money. The right product at the right price, like magic.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/trending"
              data-testid="hero-cta-trending"
              className="inline-flex items-center gap-2 rounded-xl bg-gold-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-gold-700 hover:shadow-md"
            >
              <TrendingUp size={16} /> Shop Trending
            </Link>
            <Link
              to="/deals"
              data-testid="hero-cta-deals"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-navy-900 px-6 py-3 text-sm font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
            >
              <Flame size={16} /> Today's Deals
            </Link>
          </div>
          <form onSubmit={submitSearch} role="search" data-testid="hero-search-form" className="mt-6 w-full max-w-lg">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy-300" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search 100+ Amazon finds, deals & categories…"
                data-testid="hero-search-input"
                className="w-full rounded-full border-2 border-navy-200 bg-white py-3.5 pl-11 pr-28 text-sm shadow-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-300"
              />
              <button
                type="submit"
                data-testid="hero-search-button"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-gold-500 px-5 py-2 text-sm font-bold text-navy-900 transition-colors hover:bg-gold-400"
              >
                Search
              </button>
            </div>
          </form>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold text-slate-500">
            <span className="flex items-center gap-1.5"><Truck size={14} className="text-gold-500" /> Ships from official merchants</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-gold-500" /> No extra cost — ever</span>
            <span className="flex items-center gap-1.5"><Timer size={14} className="text-gold-500" /> Deals refresh daily</span>
          </div>
        </div>
        <div className="relative">
          <img
            src={HERO_IMG}
            alt="Curated shopping deals and trending products"
            className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lg"
          />
          <div className="absolute -bottom-4 left-4 rounded-xl bg-white px-4 py-3 shadow-lg">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Deal clock resets in</p>
            <p className="font-mono text-xl font-extrabold text-gold-600" data-testid="deal-countdown">{countdown}</p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="featured-categories">
        <SectionHead title="Shop by Category" sub="Eight aisles, zero overwhelm." />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.image} alt={`${c.name} category`} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="p-3">
                <h3 className="text-sm font-bold text-slate-900 group-hover:text-gold-600 sm:text-base">{c.name}</h3>
                <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{c.subcategories?.slice(0, 2).join(" · ")}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Fresh Amazon Finds — motion marquee */}
      {amazonFinds.length > 0 && (
        <section className="overflow-hidden py-8" data-testid="amazon-finds-marquee">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHead
              title="Fresh Amazon Finds"
              sub="Hand-picked by our curators — new links land here first. Hover to pause, click to shop."
              to="/search?q=amazon"
              linkLabel="Browse all"
              testid="amazon-finds-view-all"
            />
          </div>
          <div className="marquee-pause overflow-hidden">
            <div className="animate-marquee flex w-max gap-4 px-4">
              {[...amazonFinds, ...amazonFinds].map((p, i) => (
                <div key={`${p.slug}-${i}`} className="w-44 shrink-0 sm:w-52">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trending */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="trending-section">
        <SectionHead title="Trending Now" sub="What everyone's clicking this week." to="/trending" linkLabel="View all" testid="trending-view-all" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
          {trending.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
      </section>

      {/* Daily deals */}
      <section className="bg-gradient-to-r from-orange-50 to-gold-50 py-10" data-testid="daily-deals-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl md:text-3xl">
                <Flame className="text-orange-600" /> Daily Deals
              </h2>
              <p className="mt-1 text-sm text-slate-500">Fresh discounts every day — gone at midnight.</p>
            </div>
            <div className="rounded-xl bg-white px-4 py-2 text-center shadow-sm">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ends in</p>
              <p className="font-mono text-lg font-extrabold text-orange-600" data-testid="deals-countdown">{countdown}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
            {deals.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
          <div className="mt-6 text-center">
            <Link to="/deals" data-testid="deals-view-all" className="inline-flex items-center gap-2 rounded-xl bg-navy-900 px-6 py-3 text-sm font-bold text-white hover:bg-navy-700">
              See all deals <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <AdSlot slot="mid-page" minH="min-h-[100px]" />
      </div>

      {/* Recommended */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="recommended-section">
        <SectionHead title="Recommended For You" sub="Hand-picked high performers with the best review profiles." />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
          {recommended.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
      </section>

      {/* Blog highlights */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="blog-highlights">
        <SectionHead title="From the Blog" sub="Buying guides, comparisons, and honest reviews." to="/blog" linkLabel="All articles" testid="blog-view-all" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {posts.slice(0, 3).map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <span className="text-xs font-bold uppercase tracking-wider text-gold-600">{post.category}</span>
                <h3 className="mt-1 line-clamp-2 text-base font-bold text-slate-900 group-hover:text-gold-600">{post.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-slate-500">{post.excerpt}</p>
                <p className="mt-2 text-xs text-slate-400">{post.author} · {post.read_time} min read</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="newsletter-section">
        <div className="rounded-2xl bg-navy-900 px-6 py-10 text-center sm:px-12">
          <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">Deals this good shouldn't wait for payday</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
            One short email a week: the best new deals, trending products, and buying guides. No spam, unsubscribe anytime.
          </p>
          <div className="mt-6 flex justify-center">
            <NewsletterForm />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <AdSlot slot="footer" minH="min-h-[90px]" />
      </div>
    </div>
  );
}
